import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
// import { useLocalSearchParams, useNavigation, router } from 'expo-router';
import { YStack, Button, Spinner } from 'tamagui';
// import { MaterialIcons } from '@expo/vector-icons';

import {
  CobuContainer,
  CobuHeader,
  CobuBackButton,
  CobuText,
  CobuDisabledOverlay,
  CobuLoadingOverlay,
  GroupDetailSkeleton,
} from '@cobu/ui';
import { GroupDetailHeader } from '../../components/groups/GroupDetailHeader';
import { GroupDetailInfo } from '../../components/groups/GroupDetailInfo';
import GroupDiscussion from '../../components/chat/groupDiscussion/GroupDiscussion';
import {useGroupDetail} from '../../hooks/groups/useGroupDetail';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { showErrorMessage } from '../../utils/messageToast';
import { Strings } from '../../constants';
import { useTheme } from 'tamagui';
import { PullToRefreshWrapper } from '../../components/ui/PullToRefreshWrapper';
import { useGroupDiscussion } from '../../hooks/useGroupDiscussion';
import { CommentType } from '@cobu/types';
import { logger } from '../../utils/logger';

export default function GroupDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const strings = Strings.get();
  const theme = useTheme();

  // Deep linking parameters
  const commentId = searchParams.get('commentId') || undefined;
  const scrollToComment = searchParams.get('scrollToComment') === 'true';
  const deepLinkSource = searchParams.get('source') || undefined;

  // Deep linking loading state
  const [isFindingDeepLinkedComment, setIsFindingDeepLinkedComment] = useState(false);

  const {
    // Core Data
    group,
    gatherings,
    members,
    userIsGroupMember,

    // Loading States
    isLoading,
    error,
    operationLoading,
    operationError,
    isJoining,
    isLeaving,

    // Permissions
    permissions,

    // Configurations
    groupDiscussionProps,

    // Enhanced Handlers
    handleJoinGroup,
    handleLeaveGroup,
    handleRetry,
    refetchGroup,

    // UI Resources
    // strings,
    // theme,
    handleInfoPress,
  } = useGroupDetail(id?? '');

  // Use useGroupDiscussion hook for refresh functionality
  const { refreshComments, refreshing: commentsRefreshing } = useGroupDiscussion({
    groupId: id,
    commentType: CommentType.GroupComment,
    enabled: permissions.canViewDiscussion,
  });

  const renderDeepLinkFindingOverlay = useCallback(() => {
    if (!isFindingDeepLinkedComment) return null;
    return (
      <YStack
        ai="center"
        jc="center"
        bc="$blackTransparent50"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
      >
        <YStack
          ai="center"
          jc="center"
          gap="$2"
          p="$4"
          br="$4"
          bc="$white"
          maw={280}
          mx="$4"
        >
          <Spinner size="small" color="#00B4D8" />
          <CobuText variant="body" color="$cobuDarkBlue" fos="$3" ta="center">
            {strings.events.detail.labels.findingComment}
          </CobuText>
        </YStack>
      </YStack>
    );
  }, [isFindingDeepLinkedComment]);

  // Combined refresh handler for group detail (refreshes both group data and comments)
  const handleGroupDetailRefresh = useCallback(async () => {
    if (!id || !group) return;
    
    try {
      await Promise.all([
        refetchGroup(),
        refreshComments(),
      ]);
    } catch (error) {
      logger.error('Error refreshing group detail:', error);
    }
  }, [id, group, refetchGroup, refreshComments]);

  // Combined refreshing state
  const isRefreshing = commentsRefreshing;

  const renderHeader = () => (
    <CobuHeader
      title={group ? group.name : strings.groupDetail.navigation.loading}
      renderLeft={() => <CobuBackButton onPress={() => navigate('/groups')} />}
      renderRight={group ? () => (
        <InfoOutlineIcon width={24} height={24} color={theme.white?.val} onClick={handleInfoPress} />
      ) : undefined}
    />
  );

  // Error state
  const hasShownErrorRef = useRef(false);
  useEffect(() => {
    if (error && !hasShownErrorRef.current) {
      showErrorMessage(strings.common.somethingWentWrong);
      hasShownErrorRef.current = true;
    }
  }, [error]);

  // Memoize shared header and info components to prevent duplicate rendering
  const sharedHeaderAndInfo = useMemo(() => {
    // Don't render if group is not loaded yet
    if (!group) {
      return null;
    }
    
    return (
      <YStack>
        <GroupDetailHeader
          group={group}
          userIsGroupMember={userIsGroupMember}
          isJoining={isJoining || operationLoading}
          isLeaving={isLeaving || operationLoading}
          onJoinPress={handleJoinGroup}
          onLeavePress={handleLeaveGroup}
          canJoin={permissions.canJoinGroup}
        />

        <GroupDetailInfo
          group={group}
          gatherings={gatherings}
          members={members}
          userIsGroupMember={userIsGroupMember}
          onRefresh={refetchGroup}
        />
      </YStack>
    );
  }, [
    group,
    userIsGroupMember,
    permissions.canJoinGroup,
    isJoining,
    isLeaving,
    operationLoading,
    gatherings,
    members,
    handleJoinGroup,
    handleLeaveGroup,
    refetchGroup,
  ]);

  // Show loading state - only on initial load, not during refresh
  if (isLoading && !group) {
    return (
      <CobuContainer bg="$white">
        <GroupDetailSkeleton />
      </CobuContainer>
    );
  }

  if (error) {
    return null;
  }

  return (
    <PullToRefreshWrapper
      onRefresh={handleGroupDetailRefresh}
      isRefreshing={isRefreshing}
      useWindowScroll={true}
    >
      <CobuContainer bg="$white" pos="relative">
        {renderHeader()}
        {/* Show group header and info for non-members who can't see discussions */}
        {!permissions.canViewDiscussion && sharedHeaderAndInfo}

        {/* Group Discussion - only show to members or building-wide groups */}
        {permissions.canViewDiscussion && (
          <GroupDiscussion
            {...groupDiscussionProps}
            header={sharedHeaderAndInfo}
            targetCommentId={commentId}
            scrollToComment={scrollToComment}
            skipInitialDeepLinkRefresh={deepLinkSource === 'notification'}
            onDeepLinkLoadingChange={setIsFindingDeepLinkedComment}
            onComposeComment={() => navigate({
              pathname: '/chat/compose-comment',
              search: new URLSearchParams({
                groupId: group.uuid,
                enableMarketplaceModerator: 'false',
              }).toString(),
            })}
          />
        )}

        {/* Disabled Overlay for Non-Members (excluding building-wide groups) */}
        {permissions.showDisabledOverlay && permissions.canJoinGroup && (
          <CobuDisabledOverlay
            onButtonPress={handleJoinGroup}
            buttonText={strings.groupDetail.actions.joinGroup}
            disabled={isJoining || operationLoading}
            loading={isJoining || operationLoading}
          />
        )}

        {/* Full-Screen Loading Overlay for Operations */}
        <CobuLoadingOverlay
          visible={operationLoading}
          text={isJoining || operationLoading ? strings.groupDetail.actions.joining : isLeaving ? strings.groupDetail.actions.leaving : undefined}
        />

        {/* Deep Link Finding Overlay */}
        {renderDeepLinkFindingOverlay()}
      </CobuContainer>
    </PullToRefreshWrapper>
  );
} 